import React, { useCallback, useEffect, useRef, useState } from "react";

import {

  fetchOrganizationCredentialPolicy,

  getCredentialPolicySuccessMessage,

  parseCredentialPolicy,

  saveOrganizationCredentialPolicy,

  validateOrganizationCredentialPolicyForm,

} from "../../../services/credentialPolicyService";

import "../SuperAdmin/OrganizationSetup.scss";



const OrganizationCredentialPolicySection = ({

  organizationId,

  editorStaffId,

  showAlert,

  isActive = true,

}) => {

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});

  const [form, setForm] = useState({

    isPolicyEnabled: false,

    credentialExpiryDays: "",

    reminderDaysBeforeExpiry: "7",

    forceChangeOnFirstLogin: true,

  });



  const expiryInputRef = useRef(null);

  const reminderInputRef = useRef(null);

  const showAlertRef = useRef(showAlert);

  showAlertRef.current = showAlert;



  const loadPolicy = useCallback(async () => {

    if (!organizationId || !isActive) return;

    setLoading(true);

    try {

      const response = await fetchOrganizationCredentialPolicy(organizationId);

      const parsed = parseCredentialPolicy(response);

      if (!parsed.ok) {

        showAlertRef.current({

          type: "error",

          title: "Unable to load policy",

          message: parsed.message,

        });

        return;

      }

      setForm({

        isPolicyEnabled: parsed.isPolicyEnabled,

        credentialExpiryDays: parsed.credentialExpiryDays ?? "",

        reminderDaysBeforeExpiry: parsed.reminderDaysBeforeExpiry ?? "7",

        forceChangeOnFirstLogin: parsed.forceChangeOnFirstLogin,

      });

      setFieldErrors({});

    } catch (err) {

      showAlertRef.current({

        type: "error",

        title: "Unable to load policy",

        message:

          err?.message ||

          "We could not load your organisation credential policy. Please try again.",

      });

    } finally {

      setLoading(false);

    }

  }, [organizationId, isActive]);



  useEffect(() => {

    loadPolicy();

  }, [loadPolicy]);



  const showInlineFieldError = (field, message) => {

    setFieldErrors({ [field]: message });

    const ref = field === "reminderDaysBeforeExpiry" ? reminderInputRef : expiryInputRef;

    window.requestAnimationFrame(() => {

      ref.current?.focus();

      ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });

    });

  };



  const handleSave = async () => {

    const validation = validateOrganizationCredentialPolicyForm(form);

    if (!validation.valid) {

      showInlineFieldError(validation.field, validation.message);

      return;

    }



    setFieldErrors({});

    setSaving(true);

    try {

      const response = await saveOrganizationCredentialPolicy({

        Action: "Save",

        OrganizationId: organizationId,

        IsPolicyEnabled: form.isPolicyEnabled,

        PasswordExpiryDays: validation.expiryDays,

        AccessCodeExpiryDays: validation.expiryDays,

        ReminderDaysBeforeExpiry: validation.reminderDays,

        ForceChangeOnFirstLogin: form.forceChangeOnFirstLogin,

        UpdatedBy: editorStaffId,

      });

      if (response.success !== 1) {

        showAlertRef.current({

          type: "error",

          title: "Unable to save policy",

          message:

            response.message || "We could not save your credential policy. Please try again.",

        });

        return;

      }

      showAlertRef.current({

        type: "success",

        title: "Policy updated",

        message:

          response.message && response.message !== "OK"

            ? response.message

            : getCredentialPolicySuccessMessage(),

      });

      await loadPolicy();

    } catch (err) {

      showAlertRef.current({

        type: "error",

        title: "Unable to save policy",

        message: err?.message || "We could not save your credential policy. Please try again.",

      });

    } finally {

      setSaving(false);

    }

  };



  if (!isActive) {

    return null;

  }



  if (loading) {

    return <p className="text-muted mb-0">Loading credential policy…</p>;

  }



  return (

    <div className="org-setup-panel">

      <p className="org-setup-panel-intro">

        Configure organisation-wide credential expiry for password and access code together.

        Users receive a reminder before expiry, then must update credentials when expired.

      </p>



      <div className="alert alert-info org-setup-policy-note mb-3" role="note">

        <strong>System Administrator exemption:</strong> expiry reminders and forced expiry do not

        apply to users with the System Administrator role. All other users in this organisation

        must comply with these rules.

      </div>



      <div className="row g-3">

        <div className="col-md-12">

          <div className="form-check">

            <input

              id="credPolicyEnabled"

              className="form-check-input"

              type="checkbox"

              checked={form.isPolicyEnabled}

              onChange={(e) => {

                const isPolicyEnabled = e.target.checked;

                setForm((p) => ({ ...p, isPolicyEnabled }));

                if (!isPolicyEnabled) {

                  setFieldErrors({});

                }

              }}

            />

            <label className="form-check-label" htmlFor="credPolicyEnabled">

              Enable credential expiry policy

            </label>

          </div>

        </div>



        <div className="col-md-6">

          <label className="form-label" htmlFor="credPolicyExpiryDays">

            Credential expiry (days)

          </label>

          <input

            id="credPolicyExpiryDays"

            ref={expiryInputRef}

            type="number"

            min="1"

            step="1"

            className={`form-control ${fieldErrors.credentialExpiryDays ? "is-invalid" : ""}`}

            value={form.credentialExpiryDays}

            aria-invalid={fieldErrors.credentialExpiryDays ? "true" : "false"}

            aria-describedby={

              fieldErrors.credentialExpiryDays ? "credPolicyExpiryError" : "credPolicyExpiryHelp"

            }

            onChange={(e) => {

              setForm((p) => ({ ...p, credentialExpiryDays: e.target.value }));

              if (fieldErrors.credentialExpiryDays) {

                setFieldErrors((prev) => ({ ...prev, credentialExpiryDays: "" }));

              }

            }}

            onBlur={() => {

              if (!form.isPolicyEnabled) return;

              const validation = validateOrganizationCredentialPolicyForm(form);

              if (!validation.valid && validation.field === "credentialExpiryDays") {

                setFieldErrors((prev) => ({

                  ...prev,

                  credentialExpiryDays: validation.message,

                }));

              }

            }}

            disabled={!form.isPolicyEnabled}

            placeholder="e.g. 15"

          />

          {fieldErrors.credentialExpiryDays ? (

            <div id="credPolicyExpiryError" className="org-setup-field-error" role="alert">

              <i className="fas fa-exclamation-circle" aria-hidden="true" />

              <span>{fieldErrors.credentialExpiryDays}</span>

            </div>

          ) : (

            <div id="credPolicyExpiryHelp" className="form-text">

              Password and access code must be renewed within this many days. Minimum 1 day.

            </div>

          )}

        </div>



        <div className="col-md-6">

          <label className="form-label" htmlFor="credPolicyReminderDays">

            Remind users before expiry (days)

          </label>

          <input

            id="credPolicyReminderDays"

            ref={reminderInputRef}

            type="number"

            min="1"

            step="1"

            className={`form-control ${fieldErrors.reminderDaysBeforeExpiry ? "is-invalid" : ""}`}

            value={form.reminderDaysBeforeExpiry}

            aria-invalid={fieldErrors.reminderDaysBeforeExpiry ? "true" : "false"}

            aria-describedby={

              fieldErrors.reminderDaysBeforeExpiry

                ? "credPolicyReminderError"

                : "credPolicyReminderHelp"

            }

            onChange={(e) => {

              setForm((p) => ({ ...p, reminderDaysBeforeExpiry: e.target.value }));

              if (fieldErrors.reminderDaysBeforeExpiry) {

                setFieldErrors((prev) => ({ ...prev, reminderDaysBeforeExpiry: "" }));

              }

            }}

            onBlur={() => {

              if (!form.isPolicyEnabled) return;

              const validation = validateOrganizationCredentialPolicyForm(form);

              if (!validation.valid && validation.field === "reminderDaysBeforeExpiry") {

                setFieldErrors((prev) => ({

                  ...prev,

                  reminderDaysBeforeExpiry: validation.message,

                }));

              }

            }}

            disabled={!form.isPolicyEnabled}

            placeholder="e.g. 7"

          />

          {fieldErrors.reminderDaysBeforeExpiry ? (

            <div id="credPolicyReminderError" className="org-setup-field-error" role="alert">

              <i className="fas fa-exclamation-circle" aria-hidden="true" />

              <span>{fieldErrors.reminderDaysBeforeExpiry}</span>

            </div>

          ) : (

            <div id="credPolicyReminderHelp" className="form-text">

              Example: expiry 15 days and reminder 7 days means warnings from day 8 until expiry.

              Must be less than expiry days.

            </div>

          )}

        </div>



        <div className="col-md-12">

          <div className="form-check">

            <input

              id="credForceFirstLogin"

              className="form-check-input"

              type="checkbox"

              checked={form.forceChangeOnFirstLogin}

              onChange={(e) => setForm((p) => ({ ...p, forceChangeOnFirstLogin: e.target.checked }))}

            />

            <label className="form-check-label" htmlFor="credForceFirstLogin">

              Force credential change after first login (after T&amp;C acceptance)

            </label>

          </div>

        </div>

      </div>



      <div className="mt-3">

        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>

          {saving ? "Saving…" : "Save credential policy"}

        </button>

      </div>

    </div>

  );

};



export default OrganizationCredentialPolicySection;

